import { memo, useEffect, useState } from "react"
import { z } from "zod"

import { Button, Dialog, Form, FormInput, useZodForm } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { toast } from "@/components/toast"
import {
	EMAIL_MAXIMUM_LENGTH,
	EMAIL_MAXIMUM_LENGTH_ERROR_MESSAGE,
} from "@/constants"
import { api } from "@/lib/api"
import { useZero } from "@/lib/zero"
import { cleanExternalIds, externalIdsChanged } from "../customers/external-ids"
import { ExternalIdsField } from "../customers/external-ids-field"

interface UpdateContactDialogProps {
	values: UpdateContactFormValues
	isOpen: boolean
	onOpenChange: (value: boolean) => void
}

const UpdateContactFormSchema = z.object({
	id: z.string().min(1),
	name: z.string().min(1),
	email: z
		.string()
		.email()
		.max(EMAIL_MAXIMUM_LENGTH, EMAIL_MAXIMUM_LENGTH_ERROR_MESSAGE),
})
type UpdateContactFormValues = z.infer<typeof UpdateContactFormSchema>

export const UpdateContactDialog = memo(
	({ values, onOpenChange, isOpen }: UpdateContactDialogProps) => {
		const zero = useZero()

		const form = useZodForm({
			schema: UpdateContactFormSchema,
			defaultValues: values,
		})

		const utils = api.useUtils()
		const savedExternalIds = api.contact.externalIds.useQuery(
			{ id: values.id },
			{ enabled: isOpen },
		)
		const setExternalIds = api.contact.setExternalIds.useMutation()
		const [externalIds, setExternalIdsDraft] = useState<string[]>([])
		useEffect(() => {
			if (isOpen && savedExternalIds.data)
				setExternalIdsDraft(savedExternalIds.data)
		}, [isOpen, savedExternalIds.data])

		return (
			<Dialog
				isOpen={isOpen}
				title="Update contact"
				onOpenChange={onOpenChange}
				footer={
					<Button form="update-contact-form" type="submit">
						Submit
					</Button>
				}
			>
				<Form
					id="update-contact-form"
					form={form}
					className="space-y-4"
					onSubmit={async (values) => {
						const existingContacts = await zero.run(
							queries.contactByEmail(values.email),
							{ type: "complete" },
						)
						if (existingContacts && existingContacts.id !== values.id) {
							toast.error({
								title: "Failed to update contact",
								description: "Email is already taken",
							})
							return
						}
						const result = await zero.mutate(
							mutators.contacts.update({
								id: values.id,
								name: values.name,
								email: values.email,
							}),
						).client
						if (result.type !== "success") {
							toast.error({
								title: "Failed to update contact",
								description:
									result.error instanceof Error
										? result.error.message
										: "Unknown error",
							})
							return
						}

						const saved = savedExternalIds.data
						if (saved && externalIdsChanged(saved, externalIds)) {
							try {
								const next = await setExternalIds.mutateAsync({
									id: values.id,
									externalIds: cleanExternalIds(externalIds),
								})
								utils.contact.externalIds.setData({ id: values.id }, next)
							} catch (error) {
								toast.error({
									title: "External IDs were not saved",
									description:
										error instanceof Error ? error.message : undefined,
								})
								return
							}
						}
						onOpenChange(false)
					}}
				>
					<FormInput name="name" label="Name" />
					<FormInput name="email" label="Email" />
					<ExternalIdsField
						value={externalIds}
						onChange={setExternalIdsDraft}
						isLoading={savedExternalIds.isLoading}
					/>
				</Form>
			</Dialog>
		)
	},
)
